import { Show } from 'solid-js';
import { radioStore } from '~/stores/radioStore';
import { RadioStationSelector } from './RadioStationSelector';
import '~/styles/radio-player.css';

function PlayIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
    </svg>
  );
}

/** Эквалайзер: три анимированных столбика, рендерится в чёрном квадрате
 *  Play-кнопки, когда радио действительно играет. */
function EqualizerIcon() {
  return (
    <span class="radio__eq" aria-hidden="true">
      <i />
      <i />
      <i />
    </span>
  );
}

/** В играющем состоянии — эквалайзер. В паузе/idle — иконка Play.
 *  Иконка Pause остаётся для kebd-фокуса в loading-стейте. */
function PlayBtnContent(props: { playing: boolean }) {
  return (
    <Show when={props.playing} fallback={<PlayIcon />}>
      <EqualizerIcon />
    </Show>
  );
}

function ChevronIcon(props: { dir: 'up' | 'down' }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <polyline points={props.dir === 'up' ? '6 15 12 9 18 15' : '6 9 12 15 18 9'} />
    </svg>
  );
}

function VolumeIcon(props: { muted: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="none" />
      <Show when={!props.muted} fallback={<><line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" /></>}>
        <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
      </Show>
    </svg>
  );
}

function RadioIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M2 16V8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2z" />
      <circle cx="7" cy="14" r="2.5" />
      <path d="M14 10h6M14 13h6M14 16h6" />
      <path d="M7 6l8-3" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

function StatusBadge() {
  const s = radioStore.status;
  return (
    <Show when={s() === 'loading'}>
      <span class="radio__status" aria-live="polite">
        <span class="radio__status-dot" />
        Загрузка…
      </span>
    </Show>
  );
}

export function RadioPlayer() {
  const handleVolume = (e: InputEvent) => {
    const v = parseFloat((e.target as HTMLInputElement).value);
    radioStore.changeVolume(v);
  };

  const handlePlayToggle = (e: MouseEvent) => {
    e.preventDefault();
    radioStore.togglePlay();
  };

  const isPlaying = () => radioStore.status() === 'playing';
  const isLoading = () => radioStore.status() === 'loading';
  const hasError = () => radioStore.status() === 'error';

  /** Подсказка показывается, пока пользователь ни разу не нажал Play
   *  в этой сессии. После активации скрывается навсегда. */
  const showHint = () => !radioStore.userActivated();

  return (
    <div
      class="radio"
      classList={{
        'radio--collapsed': radioStore.collapsed(),
        'radio--playing': isPlaying(),
        'radio--error': hasError(),
        'radio--idle': !radioStore.userActivated() && !isLoading() && !hasError(),
      }}
      role="region"
      aria-label="Интернет-радио"
    >
      <Show when={showHint()}>
        <div class="radio__hint" role="note" aria-live="polite">
          <span>Нажми на ▶ чтобы включить радио</span>
        </div>
      </Show>
      <Show
        when={radioStore.collapsed()}
        fallback={
          <div class="radio__panel">
            <div class="radio__head">
              <div class="radio__head-title">
                <span class="radio__head-icon" aria-hidden="true"><RadioIcon /></span>
                <span>Интернет-радио</span>
              </div>
              <button
                type="button"
                class="radio__icon-btn"
                aria-label="Свернуть плеер"
                onClick={() => radioStore.setCollapsed(true)}
              >
                <ChevronIcon dir="down" />
              </button>
            </div>

            <RadioStationSelector />

            <div class="radio__controls">
              <button
                type="button"
                class="radio__play radio__play--lg"
                aria-label={isPlaying() ? 'Поставить на паузу' : 'Воспроизвести радио'}
                aria-pressed={isPlaying()}
                onClick={handlePlayToggle}
                disabled={isLoading()}
              >
                <PlayBtnContent playing={isPlaying()} />
              </button>

              <div class="radio__volume">
                <button
                  type="button"
                  class="radio__icon-btn"
                  aria-label={radioStore.muted() ? 'Включить звук' : 'Отключить звук'}
                  aria-pressed={radioStore.muted()}
                  onClick={() => radioStore.toggleMute()}
                >
                  <VolumeIcon muted={radioStore.muted()} />
                </button>
                <label class="radio__volume-slider">
                  <span class="sr-only">Громкость</span>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={radioStore.muted() ? 0 : radioStore.volume()}
                    onInput={handleVolume}
                    aria-label="Громкость"
                  />
                </label>
              </div>
            </div>

            <Show when={hasError()}>
              <div class="radio__error" role="alert">
                <p>{radioStore.error() ?? 'Не удалось подключиться к эфиру.'}</p>
                <button
                  type="button"
                  class="radio__retry"
                  onClick={() => radioStore.retry()}
                >
                  Повторить
                </button>
              </div>
            </Show>

            <Show when={isLoading()}>
              <StatusBadge />
            </Show>
          </div>
        }
      >
        <div class="radio__pill">
          <button
            type="button"
            class="radio__play"
            aria-label={isPlaying() ? 'Поставить на паузу' : 'Воспроизвести радио'}
            aria-pressed={isPlaying()}
            onClick={handlePlayToggle}
            disabled={isLoading()}
          >
            <PlayBtnContent playing={isPlaying()} />
          </button>

          <button
            type="button"
            class="radio__pill-info"
            aria-label="Развернуть плеер радио"
            onClick={() => radioStore.setCollapsed(false)}
          >
            <span class="radio__pill-icon" aria-hidden="true"><RadioIcon /></span>
            <span class="radio__pill-text">
              <span class="radio__pill-name">{radioStore.station().name}</span>
              <Show when={isLoading()} fallback={<span class="radio__pill-sub">{radioStore.station().subtitle}</span>}>
                <span class="radio__pill-sub radio__pill-sub--load">загрузка эфира…</span>
              </Show>
            </span>
          </button>

          <button
            type="button"
            class="radio__icon-btn"
            aria-label="Развернуть плеер радио"
            onClick={() => radioStore.setCollapsed(false)}
          >
            <ChevronIcon dir="up" />
          </button>
        </div>

        <Show when={hasError()}>
          <div class="radio__pill-error" role="alert">
            Ошибка эфира.{' '}
            <button type="button" onClick={() => radioStore.retry()}>
              Повторить
            </button>
          </div>
        </Show>
      </Show>
    </div>
  );
}
